import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
    ArrowLeft, 
    Calendar, 
    ChevronLeft, 
    ChevronRight,
    DollarSign,
    FileText,
    CheckCircle,
    Clock,
    AlertTriangle,
    XCircle,
    CircleDot,
    Building2,
    Filter,
    Users
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Month names in Spanish
const MONTHS = [
    'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
    'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'
];

// Estado colors and icons
const getEstadoConfig = (estado) => {
    const configs = {
        'servicio_en_curso': { 
            color: 'bg-[#5B9A9A]', 
            bgLight: 'bg-[#E8F4F4] border-[#A5D0D0]',
            textColor: 'text-[#3D7A7A]',
            icon: CircleDot,
            label: 'En Curso'
        },
        'servicio_terminado': { 
            color: 'bg-slate-500', 
            bgLight: 'bg-slate-50 border-slate-200',
            textColor: 'text-slate-700',
            icon: CheckCircle,
            label: 'Terminado'
        },
        'cobranza_activa': { 
            color: 'bg-amber-500', 
            bgLight: 'bg-amber-50 border-amber-200',
            textColor: 'text-amber-700',
            icon: Clock,
            label: 'Cobranza'
        },
        'facturado': { 
            color: 'bg-indigo-500', 
            bgLight: 'bg-indigo-50 border-indigo-200',
            textColor: 'text-indigo-700',
            icon: FileText,
            label: 'Facturado'
        },
        'pagado': { 
            color: 'bg-emerald-500', 
            bgLight: 'bg-emerald-50 border-emerald-200',
            textColor: 'text-emerald-700',
            icon: CheckCircle,
            label: 'Pagado'
        },
        'pendiente_pago': { 
            color: 'bg-yellow-500', 
            bgLight: 'bg-yellow-50 border-yellow-200',
            textColor: 'text-yellow-700',
            icon: Clock,
            label: 'Pend. Pago'
        },
        'pago_parcial': { 
            color: 'bg-lime-500', 
            bgLight: 'bg-lime-50 border-lime-200',
            textColor: 'text-lime-700',
            icon: DollarSign,
            label: 'Pago Parcial'
        },
        'vencido': { 
            color: 'bg-orange-500', 
            bgLight: 'bg-orange-50 border-orange-200',
            textColor: 'text-orange-700',
            icon: AlertTriangle,
            label: 'Vencido'
        },
        'en_mora': { 
            color: 'bg-red-500', 
            bgLight: 'bg-red-50 border-red-200',
            textColor: 'text-red-700',
            icon: AlertTriangle,
            label: 'En Mora'
        },
        'compromiso_pago': { 
            color: 'bg-orange-500', 
            bgLight: 'bg-orange-50 border-orange-200',
            textColor: 'text-orange-700',
            icon: Clock,
            label: 'Compromiso'
        },
        'castigado': { 
            color: 'bg-slate-700', 
            bgLight: 'bg-slate-100 border-slate-300',
            textColor: 'text-slate-800',
            icon: XCircle,
            label: 'Castigado'
        },
        'edp_enviado': { 
            color: 'bg-purple-500', 
            bgLight: 'bg-purple-50 border-purple-200',
            textColor: 'text-purple-700',
            icon: FileText,
            label: 'EDP Enviado'
        },
        'edp_aprobado': { 
            color: 'bg-teal-500', 
            bgLight: 'bg-teal-50 border-teal-200',
            textColor: 'text-teal-700',
            icon: CheckCircle,
            label: 'EDP Aprobado'
        },
        'espera_oc': { 
            color: 'bg-cyan-500', 
            bgLight: 'bg-cyan-50 border-cyan-200',
            textColor: 'text-cyan-700',
            icon: Clock,
            label: 'Espera OC'
        },
    };
    return configs[estado] || { 
        color: 'bg-gray-400', 
        bgLight: 'bg-gray-50 border-gray-200',
        textColor: 'text-gray-700',
        icon: CircleDot,
        label: estado || 'Sin Estado'
    };
};

// Format currency
const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '-';
    return new Intl.NumberFormat('es-CL', {
        style: 'currency',
        currency: 'CLP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(amount);
};

const TimelineGlobal = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [servicios, setServicios] = useState([]);
    const [clientes, setClientes] = useState([]);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [availableYears, setAvailableYears] = useState([]);
    const [selectedCliente, setSelectedCliente] = useState('all');
    const [selectedProducto, setSelectedProducto] = useState('all');
    const [selectedMonth, setSelectedMonth] = useState(null); // null = todos los meses

    useEffect(() => {
        fetchInitialData();
    }, []);

    useEffect(() => {
        fetchServiciosPorAnio(selectedYear);
    }, [selectedYear, selectedCliente]);

    const fetchInitialData = async () => {
        try {
            setLoading(true);
            
            // Fetch clients
            const clientesResponse = await axios.get(`${API_URL}/api/clientes`);
            setClientes(clientesResponse.data || []);
            
            // Determine available years
            const currentYear = new Date().getFullYear();
            const years = [];
            for (let y = currentYear - 2; y <= currentYear + 1; y++) {
                years.push(y);
            }
            setAvailableYears(years);
            
            // Fetch services for current year
            await fetchServiciosPorAnio(currentYear);
        } catch (error) {
            console.error('Error fetching initial data:', error);
            toast.error('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    const fetchServiciosPorAnio = async (year) => {
        try {
            const params = {
                periodo_anio: year,
                page_size: 100
            };
            
            if (selectedCliente && selectedCliente !== 'all') {
                params.cliente_id = selectedCliente;
            }
            
            const response = await axios.get(`${API_URL}/api/servicios`, { params });
            const items = response.data.items || response.data || [];
            setServicios(items);
        } catch (error) {
            console.error('Error fetching servicios:', error);
        }
    };

    // Get unique products from services
    const productos = useMemo(() => {
        const productosSet = new Set();
        servicios.forEach(s => {
            if (s.nombre_producto) {
                productosSet.add(s.nombre_producto);
            }
        });
        return Array.from(productosSet).sort();
    }, [servicios]);

    // Filter services by product
    const filteredServicios = useMemo(() => {
        if (selectedProducto === 'all') return servicios;
        return servicios.filter(s => s.nombre_producto === selectedProducto);
    }, [servicios, selectedProducto]);

    // Get product info when a product is selected
    const productoInfo = useMemo(() => {
        if (selectedProducto === 'all' || filteredServicios.length === 0) return null;
        const firstService = filteredServicios[0];
        return {
            nombre_producto: firstService.nombre_producto,
            cliente_nombre: firstService.cliente_nombre,
            cliente_id: firstService.cliente_id
        };
    }, [selectedProducto, filteredServicios]);

    // Group services by month - when product is selected, get one service per month
    const serviciosPorMes = useMemo(() => {
        const grouped = {};
        for (let m = 1; m <= 12; m++) {
            const monthServices = filteredServicios.filter(s => s.periodo_mes === m);
            grouped[m] = monthServices;
        }
        return grouped;
    }, [filteredServicios]);

    // Calculate totals
    const totals = useMemo(() => {
        const total = filteredServicios.reduce((sum, s) => sum + (parseFloat(s.monto_total) || 0), 0);
        const pagado = filteredServicios.filter(s => s.estado === 'pagado').length;
        const pendiente = filteredServicios.filter(s => !['pagado', 'castigado'].includes(s.estado)).length;
        return { total, pagado, pendiente, count: filteredServicios.length };
    }, [filteredServicios]);

    const handleYearChange = (direction) => {
        const newYear = selectedYear + direction;
        if (availableYears.includes(newYear)) {
            setSelectedYear(newYear);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5B9A9A]"></div>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-6 space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => {
                            if (selectedProducto !== 'all') {
                                setSelectedProducto('all');
                                setSelectedMonth(null);
                            } else {
                                navigate('/servicios');
                            }
                        }}
                        data-testid="back-to-servicios-btn"
                    >
                        <ArrowLeft size={20} />
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900">
                            {selectedProducto !== 'all' ? 'Línea de Tiempo' : 'Línea de Tiempo Global'}
                        </h1>
                        <p className="text-slate-500 text-sm mt-1 flex items-center gap-2">
                            {productoInfo ? (
                                <>
                                    <Building2 size={14} />
                                    {productoInfo.cliente_nombre} - {productoInfo.nombre_producto}
                                </>
                            ) : (
                                'Vista general de todos los servicios por período'
                            )}
                        </p>
                    </div>
                </div>
                
                {/* Year Selector */}
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleYearChange(-1)}
                        disabled={!availableYears.includes(selectedYear - 1)}
                    >
                        <ChevronLeft size={18} />
                    </Button>
                    <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                        <SelectTrigger className="w-32">
                            <Calendar size={14} className="mr-2" />
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {availableYears.map(year => (
                                <SelectItem key={year} value={year.toString()}>
                                    {year}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleYearChange(1)}
                        disabled={!availableYears.includes(selectedYear + 1)}
                    >
                        <ChevronRight size={18} />
                    </Button>
                </div>
            </div>

            {/* Filters */}
            <Card>
                <CardContent className="p-4">
                    <div className="flex flex-wrap gap-4 items-center">
                        <div className="flex items-center gap-2">
                            <Users size={16} className="text-slate-500" />
                            <Select value={selectedCliente} onValueChange={setSelectedCliente}>
                                <SelectTrigger className="w-[220px]" data-testid="filter-cliente">
                                    <SelectValue placeholder="Filtrar por cliente" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Todos los clientes</SelectItem>
                                    {clientes.map(cliente => (
                                        <SelectItem key={cliente.id} value={cliente.id}>
                                            {cliente.razon_social}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        
                        <div className="flex items-center gap-2">
                            <Filter size={16} className="text-slate-500" />
                            <Select value={selectedProducto} onValueChange={setSelectedProducto}>
                                <SelectTrigger className="w-[220px]" data-testid="filter-producto">
                                    <SelectValue placeholder="Filtrar por producto" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Todos los productos</SelectItem>
                                    {productos.map(producto => (
                                        <SelectItem key={producto} value={producto}>
                                            {producto}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        
                        <div className="ml-auto text-sm text-slate-500">
                            {filteredServicios.length} servicios encontrados
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Timeline Legend */}
            <Card>
                <CardHeader className="py-3">
                    <CardTitle className="text-sm font-medium text-slate-600">Leyenda de Estados</CardTitle>
                </CardHeader>
                <CardContent className="py-2">
                    <div className="flex flex-wrap gap-3">
                        {['servicio_en_curso', 'facturado', 'pagado', 'en_mora', 'cobranza_activa', 'castigado'].map(estado => {
                            const config = getEstadoConfig(estado);
                            return (
                                <div key={estado} className="flex items-center gap-1.5">
                                    <div className={`w-3 h-3 rounded-full ${config.color}`}></div>
                                    <span className="text-xs text-slate-600">{config.label}</span>
                                </div>
                            );
                        })}
                    </div>
                </CardContent>
            </Card>

            {/* Timeline Grid */}
            <Card>
                <CardContent className="p-6">
                    <div className="grid grid-cols-12 gap-2 md:gap-3">
                        {MONTHS.map((month, index) => {
                            const monthNum = index + 1;
                            const monthServices = serviciosPorMes[monthNum] || [];
                            const isCurrentMonth = new Date().getMonth() === index && new Date().getFullYear() === selectedYear;
                            const isFuture = selectedYear > new Date().getFullYear() || 
                                            (selectedYear === new Date().getFullYear() && monthNum > new Date().getMonth() + 1);
                            const totalMes = monthServices.reduce((sum, s) => sum + (parseFloat(s.monto_total) || 0), 0);
                            const pagadosMes = monthServices.filter(s => s.estado === 'pagado').length;
                            const isSelected = selectedMonth === monthNum;
                            
                            // When a product is selected, show detailed view (single service per month)
                            const isProductView = selectedProducto !== 'all';
                            const singleService = isProductView && monthServices.length > 0 ? monthServices[0] : null;
                            const estadoConfig = singleService ? getEstadoConfig(singleService.estado) : null;
                            const EstadoIcon = estadoConfig?.icon || CircleDot;
                            
                            return (
                                <div 
                                    key={monthNum}
                                    onClick={() => {
                                        if (monthServices.length > 0) {
                                            if (isProductView) {
                                                // In product view, clicking navigates to service detail
                                                navigate(`/servicios/${singleService.id}`);
                                            } else {
                                                // In global view, clicking filters the table
                                                setSelectedMonth(isSelected ? null : monthNum);
                                            }
                                        }
                                    }}
                                    className={`relative flex flex-col rounded-lg border-2 transition-all min-h-[200px] ${
                                        monthServices.length > 0 
                                            ? `bg-white cursor-pointer hover:shadow-md ${
                                                isSelected 
                                                    ? 'border-blue-500 ring-2 ring-blue-300 shadow-lg' 
                                                    : isProductView && estadoConfig
                                                        ? `${estadoConfig.bgLight}`
                                                        : 'border-slate-200 hover:border-[#8BC5C5]'
                                              }` 
                                            : isFuture 
                                                ? 'bg-slate-50 border-dashed border-slate-200' 
                                                : 'bg-white border-slate-100'
                                    } ${isCurrentMonth && !isSelected ? 'ring-2 ring-blue-400 ring-offset-2' : ''}`}
                                >
                                    {/* Selected indicator */}
                                    {isSelected && !isProductView && (
                                        <div className="absolute -top-2 -right-2 w-5 h-5 bg-[#5B9A9A] rounded-full flex items-center justify-center shadow-md">
                                            <CheckCircle size={12} className="text-white" />
                                        </div>
                                    )}
                                    
                                    {/* Month Header */}
                                    <div className={`text-center py-2 border-b ${
                                        isSelected 
                                            ? 'bg-[#E8F4F4] border-[#A5D0D0]' 
                                            : isProductView && estadoConfig
                                                ? estadoConfig.bgLight
                                                : monthServices.length > 0 
                                                    ? 'bg-slate-50' 
                                                    : 'border-slate-100'
                                    }`}>
                                        <span className={`text-xs font-bold ${
                                            isSelected 
                                                ? 'text-[#3D7A7A]' 
                                                : isProductView && estadoConfig
                                                    ? estadoConfig.textColor
                                                    : monthServices.length > 0 
                                                        ? 'text-slate-700' 
                                                        : 'text-slate-400'
                                        }`}>
                                            {month}
                                        </span>
                                        {isCurrentMonth && (
                                            <span className="ml-1 text-[10px] bg-[#5B9A9A] text-white px-1.5 py-0.5 rounded-full">
                                                Hoy
                                            </span>
                                        )}
                                    </div>
                                    
                                    {/* Content */}
                                    <div className="flex-1 p-2 flex flex-col">
                                        {monthServices.length > 0 ? (
                                            isProductView && singleService ? (
                                                // Product Detail View - Single service per month
                                                <>
                                                    {/* Estado Icon */}
                                                    <div className="flex-1 flex flex-col items-center justify-center">
                                                        <div className={`w-10 h-10 rounded-full ${estadoConfig.color} flex items-center justify-center mb-2`}>
                                                            <EstadoIcon size={20} className="text-white" />
                                                        </div>
                                                        <span className={`text-xs font-medium ${estadoConfig.textColor}`}>
                                                            {estadoConfig.label}
                                                        </span>
                                                    </div>
                                                    
                                                    {/* Montos */}
                                                    <div className="space-y-0.5 mt-auto pt-2 border-t border-slate-100">
                                                        <div className="flex justify-between text-[9px]">
                                                            <span className="text-slate-500">Total:</span>
                                                            <span className="font-medium text-slate-700">
                                                                {formatCurrency(singleService.monto_total)}
                                                            </span>
                                                        </div>
                                                        <div className="flex justify-between text-[9px]">
                                                            <span className="text-slate-500">Pagado:</span>
                                                            <span className="font-medium text-emerald-600">
                                                                {formatCurrency((singleService.monto_total || 0) - (singleService.saldo_pendiente || singleService.monto_total || 0))}
                                                            </span>
                                                        </div>
                                                        <div className="flex justify-between text-[9px]">
                                                            <span className="text-slate-500">Pend:</span>
                                                            <span className={`font-medium ${(singleService.saldo_pendiente || 0) > 0 ? 'text-red-600' : 'text-slate-600'}`}>
                                                                {formatCurrency(singleService.saldo_pendiente || singleService.monto_total)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </>
                                            ) : (
                                                // Global View - Multiple services summary
                                                <>
                                                    {/* Service count badges */}
                                                    <div className="flex flex-wrap gap-1 mb-2 justify-center">
                                                        {monthServices.slice(0, 4).map((svc, idx) => {
                                                            const svcEstadoConfig = getEstadoConfig(svc.estado);
                                                            return (
                                                                <div 
                                                                    key={idx}
                                                                    className={`w-5 h-5 rounded-full ${svcEstadoConfig.color} flex items-center justify-center cursor-pointer hover:scale-110 transition-transform`}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        navigate(`/servicios/${svc.id}`);
                                                                    }}
                                                                    title={`${svc.nombre_producto} - ${svcEstadoConfig.label}`}
                                                                >
                                                                    <span className="text-[8px] text-white font-bold">
                                                                        {svc.nombre_producto?.charAt(0) || 'S'}
                                                                    </span>
                                                                </div>
                                                            );
                                                        })}
                                                        {monthServices.length > 4 && (
                                                            <div className="w-5 h-5 rounded-full bg-slate-300 flex items-center justify-center">
                                                                <span className="text-[8px] text-slate-600 font-bold">
                                                                    +{monthServices.length - 4}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    
                                                    {/* Stats */}
                                                    <div className="text-center mb-2">
                                                        <p className="text-lg font-bold text-slate-800">
                                                            {monthServices.length}
                                                        </p>
                                                        <p className="text-[9px] text-slate-500">servicios</p>
                                                    </div>
                                                    
                                                    {/* Montos */}
                                                    <div className="space-y-0.5 mt-auto">
                                                        <div className="flex justify-between text-[9px]">
                                                            <span className="text-slate-500">Total:</span>
                                                            <span className="font-medium text-slate-700">
                                                                {formatCurrency(totalMes)}
                                                            </span>
                                                        </div>
                                                        <div className="flex justify-between text-[9px]">
                                                            <span className="text-slate-500">Pagados:</span>
                                                            <span className="font-medium text-emerald-600">
                                                                {pagadosMes}/{monthServices.length}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </>
                                            )
                                        ) : (
                                            <div className="flex-1 flex items-center justify-center">
                                                <span className="text-[10px] text-slate-400 text-center">
                                                    {isFuture ? 'Futuro' : 'Sin registro'}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </CardContent>
            </Card>

            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-[#D5E8E8] rounded-lg">
                                <Calendar size={20} className="text-[#5B9A9A]" />
                            </div>
                            <div>
                                <p className="text-xs text-slate-500">
                                    {selectedProducto !== 'all' ? 'Períodos en' : 'Servicios en'} {selectedYear}
                                </p>
                                <p className="text-xl font-bold text-slate-900">
                                    {totals.count}
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-emerald-100 rounded-lg">
                                <CheckCircle size={20} className="text-emerald-600" />
                            </div>
                            <div>
                                <p className="text-xs text-slate-500">Pagados</p>
                                <p className="text-xl font-bold text-emerald-600">
                                    {totals.pagado}
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-amber-100 rounded-lg">
                                <Clock size={20} className="text-amber-600" />
                            </div>
                            <div>
                                <p className="text-xs text-slate-500">Pendientes</p>
                                <p className="text-xl font-bold text-amber-600">
                                    {totals.pendiente}
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-slate-100 rounded-lg">
                                <DollarSign size={20} className="text-slate-600" />
                            </div>
                            <div>
                                <p className="text-xs text-slate-500">Total Año</p>
                                <p className="text-xl font-bold text-slate-900">
                                    {formatCurrency(totals.total)}
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Services List */}
            {filteredServicios.length > 0 && (
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle className="text-lg">
                            Detalle de Servicios
                            {selectedMonth && (
                                <Badge className="ml-3 bg-[#D5E8E8] text-[#3D7A7A] hover:bg-blue-200">
                                    {MONTHS[selectedMonth - 1]} {selectedYear}
                                </Badge>
                            )}
                        </CardTitle>
                        {selectedMonth && (
                            <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => setSelectedMonth(null)}
                                className="text-slate-500 hover:text-slate-700"
                            >
                                <XCircle size={16} className="mr-1" />
                                Mostrar todos
                            </Button>
                        )}
                    </CardHeader>
                    <CardContent>
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b">
                                        <th className="text-left py-2 px-3 text-xs font-medium text-slate-500">PERÍODO</th>
                                        <th className="text-left py-2 px-3 text-xs font-medium text-slate-500">PRODUCTO</th>
                                        <th className="text-left py-2 px-3 text-xs font-medium text-slate-500">CLIENTE</th>
                                        <th className="text-right py-2 px-3 text-xs font-medium text-slate-500">MONTO</th>
                                        <th className="text-center py-2 px-3 text-xs font-medium text-slate-500">ESTADO</th>
                                        <th className="text-center py-2 px-3 text-xs font-medium text-slate-500">ACCIONES</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(selectedMonth 
                                        ? filteredServicios.filter(s => s.periodo_mes === selectedMonth) 
                                        : filteredServicios
                                    ).slice(0, 50).map((servicio) => {
                                        const estadoConfig = getEstadoConfig(servicio.estado);
                                        return (
                                            <tr 
                                                key={servicio.id} 
                                                className="border-b hover:bg-slate-50 cursor-pointer"
                                                onClick={() => navigate(`/servicios/${servicio.id}`)}
                                            >
                                                <td className="py-2 px-3 text-sm">
                                                    {MONTHS[servicio.periodo_mes - 1]} {servicio.periodo_anio}
                                                </td>
                                                <td className="py-2 px-3 text-sm font-medium text-slate-800">
                                                    {servicio.nombre_producto}
                                                </td>
                                                <td className="py-2 px-3 text-sm text-slate-600">
                                                    {servicio.cliente_nombre}
                                                </td>
                                                <td className="py-2 px-3 text-sm text-right font-medium">
                                                    {formatCurrency(servicio.monto_total)}
                                                </td>
                                                <td className="py-2 px-3 text-center">
                                                    <Badge className={`${estadoConfig.color} text-white text-[10px]`}>
                                                        {estadoConfig.label}
                                                    </Badge>
                                                </td>
                                                <td className="py-2 px-3 text-center">
                                                    <Button 
                                                        variant="ghost" 
                                                        size="sm"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            navigate(`/servicios/${servicio.id}/timeline`);
                                                        }}
                                                    >
                                                        <Calendar size={14} className="mr-1" />
                                                        Ver Timeline
                                                    </Button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            {(() => {
                                const displayedServices = selectedMonth 
                                    ? filteredServicios.filter(s => s.periodo_mes === selectedMonth)
                                    : filteredServicios;
                                return displayedServices.length > 50 && (
                                    <p className="text-center text-sm text-slate-500 py-3">
                                        Mostrando 50 de {displayedServices.length} servicios
                                    </p>
                                );
                            })()}
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
};

export default TimelineGlobal;
